// src/pages/LoginPage.js
/* ------------------------------------------------------------------ */
/* Login – email / password sign-in                                    */
/* ------------------------------------------------------------------ */
/**
 * • Validates email format + non-empty password.
 * • Calls `signInWithEmailAndPassword`; redirects to `/home` on success.
 * • Displays modal errors via <ErrorPopup/>.
 * • NEW: eye / eye-off button toggles password visibility.
 *
 * @returns {JSX.Element}
 */

import React, { useState, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";

import { useTranslation } from "react-i18next";
import HeroBackground from "../components/HeroBackground";
import usePopup from "../hooks/usePopup";
import { firebaseApp } from "../firebase/firebaseApp";
import { EMAIL_REGEX } from "../utils/constants";
import { friendlyMessage } from "../utils/authentication/errorMessages";

/* Firebase singleton (SDK init is idempotent) */
const auth = getAuth(firebaseApp);

const SPLASH_MS = 1000; // how long the splash is visible
const FADE_MS = 220; // fade-out duration (keep <= SPLASH_MS)

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { popup, open: openPopup, close: closePopup } = usePopup();

  const [loading, setLoading] = useState(false);
  const [viewPassword, setViewPassword] = useState(false);
  const [credentials, setCredentials] = useState({ email: "", password: "" });

  /* Two-way bind inputs */
  const handleChange = (key) => (e) =>
    setCredentials((prev) => ({ ...prev, [key]: e.target.value }));

  /* Submit */
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      closePopup();

      /* client-side checks */
      if (!EMAIL_REGEX.test(credentials.email)) {
        openPopup({ message: t("login.emailRegexError") });
        return;
      }
      if (!credentials.password) {
        openPopup({ message: t("login.missingPasswordError") });
        return;
      }

      /* Firebase sign-in */
      setLoading(true);
      try {
        await signInWithEmailAndPassword(
          auth,
          credentials.email,
          credentials.password
        );
        navigate("/home", { replace: true });
      } catch (err) {
        openPopup({ message: friendlyMessage(err.code) });
      } finally {
        setLoading(false);
      }
    },
    [credentials, navigate, closePopup, openPopup]
  );

  return (
    <div className="w-full lg:w-2/3 h-full flex flex-col justify-between lg:justify-center items-center gap-4 text-greyscale-1">
      <HeroBackground
        poster="/brand/CoverHero.png"
        videoSrc="/brand/HeroVideo.mp4"
      />

      <div className="flex flex-col items-center justify-center z-10 h-fit w-full">
        <img
          src="/logos/BreatheUltra_Main_Logo.png"
          className="w-2/3 h-auto max-w-lg"
          alt="PDX logo"
        />
      </div>
      <div className="flex flex-col gap-2 p-4 w-full overflow-hidden items-center justify-between max-w-lg relative bg-container rounded-t-2xl lg:rounded-2xl">
        {/* <h1 className="supertitle-text !text-primary">
          <span>{t("landing.title")}</span>
        </h1> */}
        <h2 className="subtitle-text">{t("landing.subtitle")}</h2>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-3 w-full"
          noValidate
          autoComplete="on"
        >
          <input
            type="email"
            placeholder={t("login.email")}
            className="custom-input-on-dark  w-full"
            value={credentials.email}
            onChange={handleChange("email")}
            required
          />

          {/* password + eye toggle */}
          <div className="flex flex-row gap-2">
            <input
              type={viewPassword ? "text" : "password"}
              placeholder={t("login.password")}
              className="custom-input-on-dark  w-full"
              value={credentials.password}
              onChange={handleChange("password")}
              required
            />
            <button
              type="button"
              onClick={() => setViewPassword((v) => !v)}
              className="input-toggle-button-on-dark flex items-center !p-0 "
              aria-label={viewPassword ? "Hide password" : "Show password"}
              aria-pressed={viewPassword} /* conveys on/off state */
            >
              {/* Password visibility toggle */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className=" w-8 mx-2 input-icon"
                viewBox="0 -960 960 960"
              >
                {/* SVG Paths */}
                {viewPassword ? (
                  /* eye-off path */
                  <path d="M644-428l-58-58q9-47-27-88t-93-32l-58-58q17-8 34.5-12t37.5-4q75 0 127.5 52.5T660-500q0 20-4 37.5T644-428zm128 126l-58-56q38-29 67.5-63.5T832-500q-50-101-143.5-160.5T480-720q-29 0-57 4t-55 12l-62-62q41-17 84-25.5t90-8.5q151 0 269 83.5T920-500q-23 59-60.5 109.5T772-302zm20 246L624-222q-35 11-70.5 16.5T480-200q-151 0-269-83.5T40-500q21-53 53-98.5t73-81.5L56-792l56-56 736 736-56 56zM222-624q-29 26-53 57t-41 67q50 101 143.5 160.5T480-280q20 0 39-2.5t39-5.5l-36-38q-11 3-21 4.5t-21 1.5q-75 0-127.5-52.5T300-500q0-11 1.5-21t4.5-21l-84-82zm319 93zm-151 75z"></path>
                ) : (
                  /* eye path */
                  <path d="M480-320q75 0 127.5-52.5T660-500q0-75-52.5-127.5T480-680q-75 0-127.5 52.5T300-500q0 75 52.5 127.5T480-320zm0-72q-45 0-76.5-31.5T372-500q0-45 31.5-76.5T480-608q45 0 76.5 31.5T588-500q0 45-31.5 76.5T480-392zm0 192q-146 0-266-81.5T40-500q54-137 174-218.5T480-800q146 0 266 81.5T920-500q-54 137-174 218.5T480-200zm0-300zm0 220q113 0 207.5-59.5T832-500q-50-101-144.5-160.5T480-720q-113 0-207.5 59.5T128-500q50 101 144.5 160.5T480-280z"></path>
                )}
              </svg>
            </button>
          </div>

          {/* <div className="flex justify-between">
            <Link
              to="/auth/forgot-password"
              className="muted-text !text-greyscale-1 underline hover:italic"
              disabled
            >
              {t("login.forgot")}
            </Link>
          </div> */}

          <button
            type="submit"
            className="primary-button w-full disabled:opacity-50"
            disabled={loading}
          >
            {loading ? t("login.ctaLoading") : t("login.cta")}
          </button>
        </form>
        <Link to="/auth/signup" className="link w-full text-center">
          {t("login.signup")}
        </Link>
      </div>
    </div>
  );
}
